/**
 * Onboarding cookie. A hint for the wizard about which step the user reached,
 * and nothing else. Server prerequisites always override it.
 *
 * Locked-in decision (per project memory): cookie-only, no server-side draft.
 * Device switch loses progress, an accepted trade-off for MVP.
 *
 * ─── Nothing secret goes in here, and there is no longer a place to put it ───
 *
 * This value used to carry a `draft` bag, and step 1 stashed the ten 2FA
 * recovery codes in it so that a reload before the acknowledgement did not leave
 * the user with 2FA enabled, no codes, and a wizard step they could not finish.
 *
 * A cookie is the wrong home for that, for three reasons that are properties of
 * the medium rather than of the code:
 *
 *   - It cannot be `httpOnly`, because the wizard reads it back. Any script on
 *     the origin can therefore read a live set of recovery codes, and this app
 *     renders user-supplied names verbatim.
 *   - `path=/` means the browser attaches it to every `/api/*` request, so the
 *     codes travel to the server in a header on every call the app makes and
 *     land in anything that logs headers.
 *   - It outlives the tab. Signing out clears it; closing the tab does not.
 *
 * `sessionStorage` fixes only the second and third of those. The codes would
 * still be readable by any script on the origin, and recovery codes are a
 * password-equivalent credential.
 *
 * So they are not kept on the client at all. A reload recovers by asking the
 * server for a fresh set (`POST /auth/2fa/backup-codes/reissue`), which is safe
 * precisely because the old set was never acknowledged and so was never saved by
 * anybody. The `draft` bag is gone rather than merely unused, so the next author
 * has nowhere convenient to put the next secret.
 */
export const ONBOARDING_COOKIE = 'rg-onboarding'

export interface OnboardingCookieValue {
  step: number
  /**
   * Who wrote this cookie. A browser is shared, so a signup that follows an
   * abandoned one must not resume the previous user's wizard.
   */
  userId?: string
}

/**
 * Read the cookie, but only for the user it belongs to.
 *
 * A cookie stamped with someone else, or with nobody at all (written before this
 * was scoped), is treated as not present and deleted on the way out, so a stale
 * one cannot sit there being re-checked.
 */
export function readOnboardingCookie(userId?: string): OnboardingCookieValue | null {
  if (typeof document === 'undefined') return null
  const match = document.cookie.match(
    new RegExp(`(?:^|;\\s*)${ONBOARDING_COOKIE}=([^;]+)`),
  )
  if (!match) return null
  let parsed: OnboardingCookieValue
  try {
    parsed = JSON.parse(decodeURIComponent(match[1]))
  } catch {
    return null
  }
  if (userId && parsed?.userId !== userId) {
    clearOnboardingCookie()
    return null
  }
  return parsed
}

/** Drop the cookie and everything in it. Used on sign-out and on a mismatch. */
export function clearOnboardingCookie(): void {
  writeOnboardingCookie(null)
}

export function writeOnboardingCookie(value: OnboardingCookieValue | null): void {
  if (typeof document === 'undefined') return
  if (value === null) {
    document.cookie = `${ONBOARDING_COOKIE}=; path=/; max-age=0; SameSite=Lax`
    return
  }
  // Only `step` and `userId` are ever written. Both are stated explicitly rather
  // than spread from the argument, so a caller that widens the type by accident
  // cannot smuggle a field into the jar.
  const encoded = encodeURIComponent(
    JSON.stringify({ step: value.step, userId: value.userId } satisfies OnboardingCookieValue),
  )
  // 7 day max-age. Far longer than any reasonable onboarding session, and now
  // holding nothing worth stealing.
  document.cookie = `${ONBOARDING_COOKIE}=${encoded}; path=/; max-age=604800; SameSite=Lax`
}
